import {
  Accessor,
  createEffect,
  createSignal,
  Match,
  onMount,
  Setter,
  Show,
  Switch,
} from "solid-js";
import { HubbleFullSvg } from "../../assets/assets";
import {
  isUserProfileComplete,
  SendOTPResult,
  sendOTPViaSMS,
  updateProfile,
  SubmitOTPResult,
} from "../../data/auth";
import { goBack, closeAllModals, openModalV2 } from "../../shared_states/modal";
import { showSnackBar } from "../../shared_states/snackbar";
import { classNames } from "../../utils/etc";
import { createJob } from "../../utils/job";
import { ButtonRegular, ThreeDotLoader } from "../../widgets/button";
import { Spacer } from "../../widgets/spacer";
import { config } from "~/data/config";
import { CloseIcon } from "~/assets/svg_icons/svg_icons";

export function HubbleOTPAuth(props: {
  mode: "hubble" | "sdk";
  onPhoneSubmit: (phoneNumber: string) => Promise<SendOTPResult>;
  onOtpSubmit: (
    phoneNumber: string,
    otp: string,
    otpToken: string
  ) => Promise<SubmitOTPResult>;
  onResendOtp: (phoneNumber: string) => Promise<SendOTPResult>;
  onOtpSuccess: (
    phoneNumber: string,
    submitResult: SubmitOTPResult
  ) => Promise<void>;
  phone?: string;
}) {
  const [phoneNumber, setPhoneNumber] = createSignal<string>("");
  const [error, setError] = createSignal<string | null>(null);

  onMount(() => {
    if (props.phone) {
      setPhoneNumber(props.phone);
    }
  });

  const redirectToWeb = (url: string) => {
    window.open(url, "_blank");
  };

  const otpJob = createJob({
    errorCallback: (error) => {
      setError(error.getMostSpecificErrorMessage());
    },
    initialJob: async () => {
      return props.onPhoneSubmit(phoneNumber());
    },
    successCallback: async (otpInfo) => {
      await closeAllModals();
      openModalV2(() => (
        <CollectOTP
          onOtpSubmit={props.onOtpSubmit}
          onResendOtp={props.onResendOtp}
          onOtpSuccess={props.onOtpSuccess}
          phoneNumber={phoneNumber()}
          otpInfo={otpInfo}
          mode={props.mode}
        />
      ));
    },
  });

  const [isFieldActive, setIsFieldActive] = createSignal(false);

  let phoneInput: HTMLInputElement | undefined;
  createEffect(() => {
    phoneInput?.focus();
  });

  createEffect(() => {
    if (phoneNumber()) {
      setError(null);
    }
  });

  return (
    <div class="box-border flex flex-col items-stretch overflow-hidden bg-white p-2 ">
      <div class="flex flex-[1] flex-col rounded-bl-none rounded-br-none rounded-tl-2xl rounded-tr-2xl border border-[#e0e0e0]">
        <HubbleHeader />
        <div class="px-4">
          <span class="text-start text-h2 text-basePrimaryDark">Log in</span>
          <Spacer height={4} />
          <span class="text-normal text-textNormal">
            Continue with mobile number
          </span>
          <Spacer height={24} />
          <div
            class={`${classNames("rounded-xl border border-[#e0e0e0] px-4 py-[13px] text-h5 font-medium text-textDark outline-none", "text-f16Bold text-textDark")}`}
            style={
              error() != null
                ? { border: "1px solid rgba(224, 54, 22, 1)" }
                : isFieldActive()
                  ? { border: "1px solid rgba(33, 36, 39, 1)" }
                  : {}
            }
          >
            <form
              class="flex"
              onSubmit={(e) => {
                e.preventDefault();
                otpJob.run();
              }}
            >
              <span>{"+91"}</span>
              <input
                ref={phoneInput}
                class={`flex-1 pl-1.5 outline-none placeholder:text-f16 placeholder:text-textNormal`}
                placeholder="Enter mobile number"
                value={phoneNumber()}
                onInput={(e) => setPhoneNumber(e.currentTarget.value)}
                type="tel"
                maxlength={10}
                onFocus={() => setIsFieldActive(true)}
                onBlur={() => setIsFieldActive(false)}
              />
            </form>
          </div>
          <Show when={error() != null}>
            <div class="pt-2 text-mediumSemiBold text-errorDark">{error()}</div>
          </Show>
          <div class="flex-1" />
          <Spacer height={16} />
        </div>
      </div>
      <div class="rounded-bl-2xl rounded-br-2xl rounded-tl-none rounded-tr-none border border-[#e0e0e0] px-4">
        <Spacer height={16} />
        <p class="text-medium text-textNormal">
          {"By continuing I agree to the "}
          <span
            class="cursor-pointer text-textDark underline"
            onClick={() => {
              redirectToWeb("https://www.myhubble.money/terms-and-conditions");
            }}
          >
            terms & conditions
          </span>
          {" and "}
          <span
            class="cursor-pointer text-textDark underline"
            onClick={() => {
              redirectToWeb("https://www.myhubble.money/privacy-policy");
            }}
          >
            privacy policy
          </span>
        </p>
        <Spacer height={16} />

        <ButtonRegular
          isLoading={otpJob.jobState() == "running"}
          isEnabled={true}
          onClick={() => {
            otpJob.run();
          }}
          class="w-full"
          isInitiallyEnabled={phoneNumber().length === 0}
        >
          <span>Continue</span>
        </ButtonRegular>
        <Spacer height={16} />
      </div>
    </div>
  );
}

export function CollectOTP(props: {
  mode: "hubble" | "sdk";
  onOtpSubmit: (
    phoneNumber: string,
    otp: string,
    otpToken: string
  ) => Promise<SubmitOTPResult>;
  onResendOtp: (phoneNumber: string) => Promise<SendOTPResult>;
  onOtpSuccess: (
    phoneNumber: string,
    submitResult: SubmitOTPResult
  ) => Promise<void>;
  phoneNumber: string;
  otpInfo: SendOTPResult;
}) {
  let otpToken = props.otpInfo.otpToken;

  const [otp, setOtp] = createSignal<string>("");
  const [error, setError] = createSignal<string | null>(null);

  createEffect(() => {
    if (otp().length == 6) {
      submitJob.run();
    }
    if (otp()) {
      setError(null);
    }
  });

  const submitJob = createJob({
    errorCallback: (error) => {
      setError(error.getMostSpecificErrorMessage());
    },
    initialJob: async () => {
      const submitOtpResult = await props.onOtpSubmit(
        props.phoneNumber,
        otp(),
        otpToken
      );
      const isProfileComplete =
        props.mode == "hubble"
          ? await isUserProfileComplete(submitOtpResult.sessionId)
          : true;

      await closeAllModals();
      if (isProfileComplete) {
        await props.onOtpSuccess(props.phoneNumber, submitOtpResult);
      } else {
        openModalV2(() => (
          <ProfileForm
            onAuth={async () => {
              await closeAllModals();
              await props.onOtpSuccess(props.phoneNumber, submitOtpResult);
            }}
            sid={submitOtpResult.sessionId}
          />
        ));
      }
      return submitOtpResult;
    },
  });

  const resendOtpJob = createJob({
    errorCallback: () => {
      showSnackBar({
        message: "Failed to resend OTP",
        level: "error",
      });
    },
    initialJob: async () => {
      return props.onResendOtp(props.phoneNumber);
    },
    successCallback: async (otpInfo) => {
      otpToken = otpInfo.otpToken;
      showSnackBar({
        message: "OTP has been sent again",
        level: "success",
      });
    },
  });

  return (
    <div class="box-border flex flex-col items-stretch overflow-hidden bg-white p-2">
      <div class="flex flex-[1] flex-col rounded-bl-none rounded-br-none rounded-tl-2xl rounded-tr-2xl border border-[#e0e0e0]">
        <HubbleHeader />
        <div class="px-4">
          <span class="text-start text-h2 text-basePrimaryDark">
            Verify OTP
          </span>
          <Spacer height={8} />
          <div
            onClick={async () => {
              await closeAllModals();
              await new Promise((resolve) => setTimeout(resolve, 350));
              openModalV2(() => (
                <HubbleOTPAuth
                  mode={props.mode}
                  onPhoneSubmit={async (phoneNumber: string) =>
                    sendOTPViaSMS(phoneNumber)
                  }
                  onOtpSubmit={props.onOtpSubmit}
                  onResendOtp={props.onResendOtp}
                  onOtpSuccess={props.onOtpSuccess}
                  phone={props.phoneNumber}
                />
              ));
            }}
            class="flex cursor-pointer flex-row"
          >
            <span class="text-normal text-textNormal">
              OTP has been sent on <u>{props.phoneNumber}</u>
            </span>
            <Spacer width={8} />
            {editIcon()}
          </div>
          <Spacer height={24} />
          {OtpInput({
            otp: otp,
            setOtp: setOtp,
            error: error,
          })}
          <Spacer height={54} />
          <Show when={error() != null}>
            <div class="pt-1 text-mediumSemiBold text-errorDark">{error()}</div>
          </Show>
          <Spacer height={24} />
          <Switch>
            <Match when={resendOtpJob.jobState() == "running"}>
              <ThreeDotLoader color="#000" class="three-dot-loader" />
            </Match>
            <Match when={true}>
              <div
                onClick={() => {
                  resendOtpJob.run();
                }}
              >
                <p class="text-medium text-textNormal">
                  {"Didn't get OTP? "}
                  <span class="cursor-pointer text-medium text-textDark underline">
                    Resend OTP
                  </span>
                </p>
              </div>
            </Match>
          </Switch>
          <div class="flex-1" />
          <Spacer height={16} />
        </div>
      </div>
      <div class="rounded-bl-2xl rounded-br-2xl rounded-tl-none rounded-tr-none border border-[#e0e0e0] px-4">
        <Spacer height={16} />

        <ButtonRegular
          isLoading={submitJob.jobState() == "running"}
          isEnabled={error() == null && otp().length == 6}
          onClick={() => {
            submitJob.run();
          }}
          class="w-full"
        >
          <span>Continue</span>
        </ButtonRegular>
        <Spacer height={16} />
      </div>
    </div>
  );
}

function editIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M4.17657 14.1372L4.88808 14.3743H4.88808L4.17657 14.1372ZM4.80089 12.2642L5.5124 12.5014L4.80089 12.2642ZM5.44509 11.2219L5.97542 11.7522L5.44509 11.2219ZM9.89049 6.77647L10.4208 7.3068L9.89049 6.77647ZM11.7761 6.77647L11.2458 7.3068L11.2458 7.3068L11.7761 6.77647ZM13.2238 8.22418L13.7541 7.69385L13.2238 8.22418ZM13.2238 10.1098L13.7541 10.6401L13.2238 10.1098ZM8.77843 14.5552L8.2481 14.0249H8.2481L8.77843 14.5552ZM7.73608 15.1994L7.97325 15.9109H7.97325L7.73608 15.1994ZM5.86312 15.8237L6.10029 16.5352H6.10029L5.86312 15.8237ZM8.43672 14.8741L7.98504 14.2754L7.98504 14.2754L8.43672 14.8741ZM8.17417 15.0364L7.84064 14.3646L8.17417 15.0364ZM13.7807 8.90948L14.494 8.67772L14.494 8.67771L13.7807 8.90948ZM13.7807 9.42451L14.494 9.65627L14.494 9.65627L13.7807 9.42451ZM10.5758 6.21962L10.344 5.50633L10.344 5.50633L10.5758 6.21962ZM11.0908 6.21962L11.3226 5.50633V5.50633L11.0908 6.21962ZM4.96388 11.8261L5.63564 12.1596H5.63564L4.96388 11.8261ZM5.12615 11.5636L4.52742 11.1119L4.52742 11.1119L5.12615 11.5636ZM3.86599 15.6293L3.15956 15.8812L3.86599 15.6293ZM4.37101 16.1343L4.1191 16.8407H4.1191L4.37101 16.1343ZM13.0303 3.63666C12.7374 3.34377 12.2625 3.34377 11.9696 3.63666C11.6767 3.92956 11.6767 4.40443 11.9696 4.69732L13.0303 3.63666ZM15.303 8.03066C15.5959 8.32355 16.0707 8.32355 16.3636 8.03066C16.6565 7.73776 16.6565 7.26289 16.3636 6.97L15.303 8.03066ZM4.88808 14.3743L5.5124 12.5014L4.08938 12.027L3.46506 13.9L4.88808 14.3743ZM5.97542 11.7522L10.4208 7.3068L9.36016 6.24614L4.91476 10.6915L5.97542 11.7522ZM11.2458 7.3068L12.6935 8.75451L13.7541 7.69385L12.3064 6.24614L11.2458 7.3068ZM12.6935 9.57947L8.2481 14.0249L9.30876 15.0855L13.7541 10.6401L12.6935 9.57947ZM7.49891 14.4879L5.62594 15.1122L6.10029 16.5352L7.97325 15.9109L7.49891 14.4879ZM8.2481 14.0249C8.06793 14.205 8.02648 14.2441 7.98504 14.2754L8.8884 15.4729C9.03233 15.3643 9.1569 15.2374 9.30876 15.0855L8.2481 14.0249ZM7.97325 15.9109C8.17699 15.843 8.34621 15.7883 8.50769 15.7082L7.84064 14.3646C7.79415 14.3877 7.74063 14.4073 7.49891 14.4879L7.97325 15.9109ZM7.98504 14.2754C7.93977 14.3095 7.89143 14.3394 7.84064 14.3646L8.50769 15.7082C8.6416 15.6417 8.76905 15.5629 8.88841 15.4729L7.98504 14.2754ZM12.6935 8.75451C12.8672 8.92827 12.9653 9.02711 13.0311 9.10462C13.0912 9.17543 13.0792 9.17777 13.0674 9.14124L14.494 8.67771C14.4203 8.45091 14.2949 8.27561 14.1746 8.13385C14.0599 7.99878 13.9104 7.85011 13.7541 7.69385L12.6935 8.75451ZM13.7541 10.6401C13.9104 10.4839 14.0599 10.3352 14.1746 10.2001C14.2949 10.0584 14.4203 9.88307 14.494 9.65627L13.0674 9.19274C13.0792 9.15622 13.0912 9.15856 13.0311 9.22937C12.9653 9.30688 12.8672 9.40571 12.6935 9.57947L13.7541 10.6401ZM13.0674 9.14124C13.0728 9.15798 13.0728 9.17601 13.0674 9.19274L14.494 9.65627C14.5973 9.33827 14.5973 8.99572 14.494 8.67772L13.0674 9.14124ZM10.4208 7.3068C10.5946 7.13304 10.6934 7.03503 10.7709 6.96923C10.8417 6.90911 10.8441 6.92105 10.8075 6.93292L10.344 5.50633C10.1172 5.58002 9.94191 5.70537 9.80015 5.82572C9.66508 5.94038 9.51641 6.08988 9.36016 6.24614L10.4208 7.3068ZM12.3064 6.24614C12.1502 6.08988 12.0015 5.94038 11.8664 5.82572C11.7247 5.70537 11.5494 5.58002 11.3226 5.50633L10.859 6.93292C10.8225 6.92105 10.8249 6.90911 10.8957 6.96923C10.9732 7.03503 11.072 7.13304 11.2458 7.3068L12.3064 6.24614ZM10.8075 6.93292C10.8243 6.92748 10.8423 6.92748 10.859 6.93292L11.3226 5.50633C11.0046 5.40301 10.662 5.40301 10.344 5.50633L10.8075 6.93292ZM5.5124 12.5014C5.59298 12.2597 5.61256 12.2061 5.63564 12.1596L4.29213 11.4926C4.21195 11.6541 4.15729 11.8233 4.08938 12.027L5.5124 12.5014ZM4.91476 10.6915C4.76291 10.8434 4.636 10.968 4.52742 11.1119L5.72489 12.0152C5.75615 11.9738 5.79526 11.9324 5.97542 11.7522L4.91476 10.6915ZM5.63564 12.1596C5.66086 12.1089 5.69074 12.0605 5.72489 12.0152L4.52742 11.1119C4.43738 11.2312 4.35861 11.3587 4.29213 11.4926L5.63564 12.1596ZM3.46506 13.9C3.33407 14.2929 3.21866 14.6368 3.15318 14.9155C3.09031 15.183 3.03521 15.5325 3.15956 15.8812L4.57242 15.3774C4.60023 15.4554 4.5647 15.4659 4.61341 15.2586C4.6595 15.0625 4.74764 14.7956 4.88808 14.3743L3.46506 13.9ZM5.62595 15.1122C5.20464 15.2526 4.93782 15.3408 4.74168 15.3869C4.53442 15.4356 4.54493 15.4001 4.62292 15.4279L4.1191 16.8407C4.46782 16.9651 4.81727 16.91 5.08481 16.8471C5.36348 16.7816 5.70735 16.6662 6.10029 16.5352L5.62595 15.1122ZM3.15956 15.8812C3.31919 16.3288 3.67145 16.6811 4.1191 16.8407L4.62292 15.4279C4.59936 15.4195 4.58082 15.4009 4.57242 15.3774L3.15956 15.8812ZM11.9696 4.69732L15.303 8.03066L16.3636 6.97L13.0303 3.63666L11.9696 4.69732Z"
        fill="#212427"
      />
    </svg>
  );
}

export function ProfileForm(
  props: { onAuth: () => Promise<void>; sid: string } = {
    onAuth: async () => {},
    sid: "",
  }
) {
  const [name, setName] = createSignal<string>("");
  const [email, setEmail] = createSignal<string>("");
  const [error, setError] = createSignal<string | null>(null);

  createEffect(() => {
    if (email()) {
      setError(null);
    }
    if (name()) {
      setError(null);
    }
  });

  const submitJob = createJob({
    errorCallback: (error) => {
      setError(error.message ?? "Failed to submit profile");
    },
    initialJob: async () => {
      const splitName = name().split(" ");
      const firstName = splitName[0];
      const lastName = splitName.length > 1 ? splitName[1] : "";
      await updateProfile(firstName, lastName, email(), props.sid);
      showSnackBar({
        message: "Profile completed successfully",
        level: "success",
      });

      await props.onAuth();
    },
  });

  let nameInput: HTMLInputElement | undefined;
  onMount(() => {
    nameInput?.focus();
  });

  return (
    <div class="box-border flex flex-col items-stretch overflow-hidden bg-[white] p-2 sm:min-h-[500px] sm:min-w-[512px]">
      <div class="flex flex-[1] flex-col rounded-bl-none rounded-br-none rounded-tl-2xl rounded-tr-2xl border border-[#e0e0e0]">
        <HubbleHeader />
        <div class="px-[16px]">
          <span class="text-start text-h2 text-basePrimaryDark">
            Welcome to Hubble
          </span>
          <Spacer height={8} />
          <span class="text-normal text-textNormal">
            Lets setup your account
          </span>
          <Spacer height={24} />
          <div class="flex flex-col ">
            <span class="text-bold">Your name</span>
            <Spacer height={8} />
            <div
              class={`rounded-xl border border-baseTertiaryDark  px-4 py-[13px] ${error() && name().trim() == "" ? "border-errorDark" : ""}`}
            >
              <input
                ref={nameInput}
                class="w-full  outline-none placeholder:text-f16 placeholder:text-textNormal"
                placeholder="Enter your name"
                value={name().trimStart()}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </div>
          </div>
          <Show when={error() != null && name().trim().length == 0}>
            <div class="pt-2 text-mediumSemiBold text-errorDark">{error()}</div>
          </Show>
          <Spacer height={24} />
          <div class="flex flex-col">
            <span class="text-bold">Your email</span>
            <Spacer height={8} />
            <div
              class={`rounded-xl border border-baseTertiaryDark  px-4 py-[13px] ${error()?.includes("email") ? "border-errorDark" : ""}`}
            >
              <input
                class="w-full  outline-none placeholder:text-f16 placeholder:text-textNormal"
                placeholder="Enter your email address "
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
            </div>
          </div>
          <Show when={error() != null && name().trim().length > 0}>
            <div class="pt-2 text-mediumSemiBold text-errorDark">{error()}</div>
          </Show>
          <div class="flex-1" />
          <Spacer height={16} />
        </div>
      </div>
      <div class="rounded-bl-2xl rounded-br-2xl rounded-tl-none rounded-tr-none border border-[#e0e0e0] px-4">
        <Spacer height={16} />

        <ButtonRegular
          isLoading={submitJob.jobState() == "running"}
          isEnabled={true}
          onClick={() => {
            if (name().trim().length === 0) {
              setError("Please enter your name");
              return;
            } else {
              submitJob.run();
            }
          }}
          class="w-full"
        >
          <span>Continue</span>
        </ButtonRegular>
        <Spacer height={16} />
      </div>
    </div>
  );
}

function HubbleHeader() {
  return (
    <div class="rounded-t-[16px] bg-gradient-to-b from-[#dfedf1] to-baseTertiaryLight px-[16px]">
      <Spacer height={9} />
      <div class="flex flex-row items-center justify-between">
        <HubbleFullSvg class="h-[24px] w-[101px] text-[#ffffff30]" />
        <div class="hidden sm:block">
          <div
            class="inline-flex  h-10  w-10 translate-x-2  cursor-pointer items-center justify-center gap-2.5 rounded-3xl bg-baseTertiaryLight bg-opacity-10 "
            onclick={() => {
              goBack();
            }}
          >
            {/* <div
              style={{
                color: "#7C7C7C",
              }}
              class="ph ph-x flex h-4 w-4 items-center justify-center  p-[5px] opacity-100"
            ></div> */}
            <CloseIcon height={22} width={22} class="text-textNormal" />
          </div>
        </div>
      </div>
      <Spacer height={24} />
    </div>
  );
}

function OtpInput(props: {
  otp: Accessor<string>;
  setOtp: Setter<string>;
  error: Accessor<string | null>;
}) {
  const [hasFocus, setHasFocus] = createSignal<boolean>(false);

  let otpInput: HTMLInputElement | undefined;
  createEffect(async () => {
    otpInput?.focus();
  });

  return (
    <div class="relative">
      <input
        ref={otpInput}
        class="absolute z-10 h-16 w-full bg-transparent text-baseTertiaryLight caret-transparent"
        type="tel"
        maxlength={6}
        value={props.otp()}
        placeholder=""
        onInput={(e) => props.setOtp(e.currentTarget.value)}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
      />
      <div class=" pointer-events-none absolute z-20 flex w-full items-center justify-around space-x-2">
        {[...Array(6)].map((_, index) => (
          <div
            class={`flex h-12 w-16 cursor-text items-center justify-center rounded-[12px] border  ${
              props.error() == null
                ? index == props.otp().length
                  ? "border-[#000]"
                  : "border-baseSecondaryLight"
                : "border-errorDark"
            }`}
          >
            {props.otp()[index] ? (
              props.otp()[index]
            ) : index == props.otp().length && hasFocus() ? (
              <div class={`animateBlink h-3 w-0.5 bg-baseDark`} />
            ) : (
              <div class="h-1 w-1 rounded-full bg-baseSecondaryLight" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
